<script setup lang="ts">
  import { ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Trash, Copy, Pen, Pause } from "lucide-vue-next";
  import { useToastsStore } from "@/stores/toastsStore";
  import { useEmailCampaignsStore } from "@/stores/survey/mailing/emailCampaigns";
  import { useSurveyStore } from "@/stores/survey/survey";
  import { IndexTable, IndexTableName, IndexTableDate, IndexTableDetail, IndexTableEmpty } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import { StatusBadge } from "@/components/episto-ui/status-badge";
  import { Button } from "@/components/ui/button";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import type { BadgeVariants } from "@/components/ui/badge";
  import type { EmailCampaign, EmailCampaignStatistics } from "@/types";

  const toast = useToastsStore();
  const { token } = storeToRefs(useSurveyStore());
  const emailCampaignsStore = useEmailCampaignsStore();
  const { emailCampaigns, emailCampaignsCount, currentPage } = storeToRefs(emailCampaignsStore);
  const { changePage, removeEmailCampaign, duplicateEmailCampaign, suspendEmailCampaign } = emailCampaignsStore;
  const { openRow } = useRowNavigation();

  const expandedKeys = ref<string[]>([]);
  const toggle = (campaign: EmailCampaign) => {
    expandedKeys.value = expandedKeys.value.includes(campaign.token) ? [] : [campaign.token];
  };

  const columns: DataTableColumn<EmailCampaign>[] = [
    { key: "name", label: "Campaign" },
    { key: "status", label: "Status", width: 110 },
    { key: "targeted", label: "Targeted", width: 110 },
    { key: "scheduled_at", label: "Scheduled", width: 190 },
  ];

  const STATUS_LABELS: Record<string, string> = { queuing: "scheduling", queued: "scheduled" };
  const STATUS_VARIANTS: Record<string, BadgeVariants["variant"]> = {
    draft: "warning",
    queuing: "info",
    queued: "info",
    sending: "purple",
    sent: "success",
    suspended: "neutral",
    error: "danger",
  };

  const isSending = (campaign: EmailCampaign) => campaign.status === "sending";
  const targeted = (campaign: EmailCampaign) =>
    campaign.statistics && campaign.mailing_list_id && campaign.delivery_mode === "batch"
      ? (campaign.statistics.targeted ?? 0).toLocaleString()
      : null;
  const editLink = (campaign: EmailCampaign) => `/surveys/${token.value}/mailing/campaigns/${campaign.token}/edit`;

  const actionsFor = (campaign: EmailCampaign) => [
    { isDelete: true, function: removeEmailCampaign, msgConfirm: "this campaign", text: "delete", icon: Trash },
    { isDelete: false, function: duplicateEmailCampaign, text: "duplicate", icon: Copy },
    { isDelete: false, function: () => openRow(editLink(campaign)), text: "edit", icon: Pen, condition: campaign.status === "queued" },
  ];

  const suspend = (campaign: EmailCampaign) => {
    suspendEmailCampaign(campaign.token);
    toast.info("Your campaign will be suspended shortly.");
  };

  const STAT_KEYS = ["delivered", "opened", "started", "completed"] as const;
  const statistics = (campaign: EmailCampaign) => {
    const stats = campaign.statistics as EmailCampaignStatistics | undefined;
    const base = stats?.targeted || campaign.contacts_count || 1;
    return STAT_KEYS.map((key) => ({
      key,
      count: (stats?.[key] ?? 0).toLocaleString(),
      percentage: (((stats?.[key] ?? 0) / base) * 100).toFixed(2),
    }));
  };
</script>

<template>
  <IndexTable
    :items="emailCampaigns"
    :columns="columns"
    :current-page="currentPage"
    :total="emailCampaignsCount"
    :expanded-keys="expandedKeys"
    actions-class="w-[150px]"
    item-key="token"
    empty-message="No campaign yet."
    @row-click="toggle"
    @page-change="changePage">
    <template #cell-name="{ item }">
      <IndexTableName :name="item.name" />
    </template>
    <template #cell-status="{ item }">
      <StatusBadge
        :status="item.status"
        :variants="STATUS_VARIANTS"
        :labels="STATUS_LABELS" />
    </template>
    <template #cell-targeted="{ item }">
      <span
        v-if="targeted(item)"
        class="tabular-nums">
        {{ targeted(item) }}
      </span>
      <IndexTableEmpty v-else />
    </template>
    <template #cell-scheduled_at="{ item }">
      <IndexTableDate
        :date="item.scheduled_at"
        format="MMM D, YYYY [at] h:mm a" />
    </template>
    <template #actions="{ item }">
      <div
        class="flex items-center justify-end gap-1"
        @click.stop>
        <Button
          v-if="item.status === 'queued'"
          variant="ghost"
          size="sm"
          class="text-primary"
          @click="suspend(item)">
          <Pause />
          Suspend
        </Button>
        <Button
          v-else
          id="campaign-card__edit-btn"
          variant="ghost"
          size="sm"
          class="text-primary"
          :disabled="isSending(item)"
          @click="openRow(editLink(item), $event)">
          <Pen />
          Edit
        </Button>
        <ActionsDropdown
          id="campaign-card__dropdown-action-btn"
          :actions="actionsFor(item)"
          :token="item.token"
          :disabled="isSending(item)" />
      </div>
    </template>
    <template #expanded="{ item }">
      <IndexTableDetail title="Sending status">
        <div
          v-if="item.status === 'sent'"
          class="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <div
            v-for="stat in statistics(item)"
            :key="stat.key"
            class="rounded-lg border bg-background px-3 py-2">
            <div class="flex items-baseline gap-1.5">
              <span class="heading-title tabular-nums">{{ stat.count }}</span>
              <span class="text-xs text-muted-foreground">{{ stat.percentage }}%</span>
            </div>
            <span class="text-xs capitalize text-muted-foreground">{{ stat.key }}</span>
          </div>
        </div>
        <p
          v-else
          class="text-13 text-muted-foreground">
          A report will be available once the campaign has been sent.
        </p>
      </IndexTableDetail>
    </template>
  </IndexTable>
</template>
